'use client';

import { type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import RichTextTitle from '../rich-text-title';
import BevSafeText from '../bev-safe-text';
import { type ConversationCardCta } from './conversation-card';

export type Props = {
	propertyName: string;
	headingLevel?: 'h1' | 'h2' | 'h3';
	sectionId?: string;
	eyebrow?: string;
	suppressEyebrow?: boolean;
	description?: string;
	links?: ConversationCardCta[];
	className?: string;
};

const slugify = (value: string) =>
	value
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

const linkClass =
	'text-button2 whitespace-nowrap text-button-primary underline underline-offset-[5px] decoration-solid [text-decoration-skip-ink:none] [text-decoration-thickness:0.1em]';

function linkAttrs(link: ConversationCardCta) {
	return {
		href: link.href,
		target: link.newWindow ? '_blank' : undefined,
		rel: link.newWindow ? 'noopener' : undefined,
	};
}

export default function Intro({
	propertyName,
	headingLevel = 'h1',
	sectionId,
	eyebrow,
	suppressEyebrow = false,
	description,
	links,
	className = '',
}: Props): ReactElement {
	const headingId = sectionId ?? slugify(propertyName);
	const showEyebrow = Boolean(eyebrow) && !suppressEyebrow;

	return (
		<section
			aria-labelledby={headingId}
			className={cn('flex flex-col items-start gap-y-stack-l', className)}
		>
			<div className="flex w-full flex-col items-start gap-y-stack-xs">
				<div className="flex w-full flex-col items-start gap-y-stack-s">
					{showEyebrow && (
						<p className="text-overline text-text-secondary">
							<BevSafeText>{eyebrow}</BevSafeText>
						</p>
					)}

					<RichTextTitle
						title={propertyName}
						id={headingId}
						headingLevel={headingLevel}
						className="text-display3 line-clamp-2 text-text-heading"
					/>
				</div>

				{links && links.length > 0 && (
					<div className="flex flex-wrap items-center gap-button-inline">
						{links.map((link, index) => (
							<a
								key={`${link.label}-${index}`}
								{...linkAttrs(link)}
								onClick={link.onClick}
								className={linkClass}
							>
								<BevSafeText>{link.label}</BevSafeText>
							</a>
						))}
					</div>
				)}
			</div>

			{description && (
				<p className="text-body2 text-text-primary">
					<BevSafeText>{description}</BevSafeText>
				</p>
			)}
		</section>
	);
}
